import { Button, UncontrolledTooltip } from "reactstrap";
import { DayCell } from "features/EmployeeAttendance/types/employee-attendance.types";

/** Types the user can click to open the attendance form. */
const INTERACTIVE_TYPES = new Set([
  "PRESENT",
  "HALF_DAY",
  "ABSENT",
  "LEAVE",
  "PENDING",
]);

function buildTooltip(cell: DayCell, disabled: boolean): string {
  switch (cell.type) {
    case "BEFORE_JOINING":
      return "Before joining date";
    case "FUTURE":
      return "Future date";
    case "WEEK_OFF":
      return "Week off";
    case "HOLIDAY": {
      const parts = [cell.holidayName?.trim() || "Holiday"];
      if (cell.holidayKind) parts.push(cell.holidayKind);
      // if (cell.isPaidHoliday) parts.push("Paid");
      return parts.join(" • ");
    }
    case "PRESENT":
      return cell.isHalfDay ? "Half day" : "Present";
    case "HALF_DAY":
      return "Half day";
    case "ABSENT":
      return "Absent";
    case "LEAVE":
      if (cell.isHalfDay) {
        return cell.leaveName?.trim()
          ? `Half day leave · ${cell.leaveName.trim()}`
          : "Half day leave";
      }
      return cell.leaveName?.trim()
        ? `On leave · ${cell.leaveName.trim()}`
        : "On leave";
    case "PENDING":
      return disabled
        ? "You do not have permission to change attendance"
        : "Click to mark attendance";
    default:
      return "";
  }
}

function buildIcon(cell: DayCell): React.ReactNode {
  switch (cell.type) {
    case "PRESENT":
      return cell.isHalfDay
        ? <i className="ri-checkbox-circle-line text-success fs-4" />
        : <i className="ri-checkbox-circle-fill text-success fs-4" />;
    case "HALF_DAY":
      return <i className="ri-checkbox-circle-line text-success fs-4" />;
    case "ABSENT":
      return <i className="ri-close-circle-fill text-danger fs-4" />;
    case "LEAVE":
      if (cell.isHalfDay) {
        return (
          <span className="position-relative d-inline-flex">
            <i className="ri-door-open-line text-primary fs-4" />
            <span
              className="position-absolute bg-info rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
              style={{ width: 11, height: 11, fontSize: 7, top: -1, right: -4, lineHeight: 1 }}
            >½</span>
          </span>
        );
      }
      return <i className="ri-door-open-line text-primary fs-4" />;
    case "HOLIDAY":
      return <i className="ri-star-s-fill text-warning fs-4" />;
    case "WEEK_OFF":
      // Finalized week-off (from DB record only — never projected from weekday logic)
      return <i className="ri-moon-fill text-muted fs-4" />;
    case "PENDING":
      return (
        <i
          className="ri-add-circle-line text-muted"
          style={{ fontSize: "1rem" }}
        />
      );
    default:
      return null;
  }
}

const AttendanceMarker = ({
  cell,
  day,
  employeeId,
  onClick,
  disabled = false,
}: {
  cell: DayCell;
  day: number;
  employeeId: number;
  onClick: () => void;
  disabled?: boolean;
}) => {
  const { type } = cell;

  // Before joining date — greyed-out dash, no interaction
  if (type === "BEFORE_JOINING") {
    return <span className="text-muted small">—</span>;
  }

  // Future date — empty light-grey circle, no interaction
  // Weekends in the future appear here too (not WEEK_OFF) until finalized
  if (type === "FUTURE") {
    return (
      <span
        className="d-inline-block rounded-circle bg-light border"
        style={{ width: 10, height: 10 }}
      />
    );
  }

  const buttonId = `calendarDay${day}emp${employeeId}`;
  const tip = buildTooltip(cell, disabled);
  const icon = buildIcon(cell);

  // Non-interactive: holidays and finalized week-offs
  if (type === "HOLIDAY" || type === "WEEK_OFF") {
    return (
      <>
        <span id={buttonId} className="d-inline-flex align-items-center">
          {icon}
        </span>
        <UncontrolledTooltip placement="top" target={buttonId}>
          {tip}
        </UncontrolledTooltip>
      </>
    );
  }

  // Interactive: PRESENT (incl. half-day), HALF_DAY, ABSENT, LEAVE, PENDING
  const canClick = !disabled && INTERACTIVE_TYPES.has(type);
  const hasValue = type !== "PENDING";

  return (
    <>
      <Button
        id={buttonId}
        className={`btn btn-sm ${hasValue ? "btn-ghost-dark" : "btn-ghost-secondary border border-dashed rounded"}`}
        style={
          !canClick
            ? { cursor: "not-allowed", pointerEvents: "auto" }
            : undefined
        }
        onClick={() => {
          if (canClick) onClick();
        }}
        title={tip}
      >
        <span className="d-inline-flex align-items-center">{icon}</span>
      </Button>
      <UncontrolledTooltip placement="top" target={buttonId}>
        {tip}
      </UncontrolledTooltip>
    </>
  );
};

export default AttendanceMarker;
